// Admin → App status (0383, mockup approved 2026-10-30: A1–A4).
//
// Maintenance: off, scheduled, on. While on, clients see the maintenance
// screen and the booking server refuses new bookings and session changes;
// every job already running carries on. "Back by" is what people are told,
// not a timer: maintenance ends only when someone presses End.
//
// Require latest version: apps built before THIS admin's release show "Please
// update". Not "now": the release's apps were built minutes before it was
// uploaded, and "now" would lock out the very release it means. The minimum is
// this admin build's time less RELEASE_SPREAD_MS, because one release builds
// its four apps one after another, a few minutes apart. So it only works from
// an admin page already on the new release, and two releases less than
// RELEASE_SPREAD_MS apart count as one.
//
// Super Admin only (Leon, 2026-10-30): the route, and the table's update
// policy. Every change is in the audit log.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Callout } from "@bednbody/ui/components/callout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import { HEADS_UP_MS, maintenanceTimeLabel } from "@bednbody/ui/lib/app-status";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type AppStatusRecord,
  useAppStatusLastChange,
  useAppStatusRecord,
  useUpdateAppStatus,
} from "./appStatus.hooks";

const DEFAULT_MESSAGE = "We're making Bednbody better. Back soon.";

const RELEASE_SPREAD_MS = 30 * 60 * 1000;
const ADMIN_BUILD_AT: string | undefined = import.meta.env.VITE_BUILD_AT;

/** The oldest build that counts as this release (see the header). */
export function releaseMinimum(adminBuildAt: string): string {
  return new Date(new Date(adminBuildAt).getTime() - RELEASE_SPREAD_MS).toISOString();
}

/** A Date as the value a `datetime-local` input takes, in this browser's time. */
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : null);

type Phase = "off" | "scheduled" | "on" | "overdue";

function phaseOf(record: AppStatusRecord, now: number): Phase {
  if (!record.maintenance_starts_at) return "off";
  if (new Date(record.maintenance_starts_at).getTime() > now) return "scheduled";
  if (record.maintenance_back_by && new Date(record.maintenance_back_by).getTime() < now)
    return "overdue";
  return "on";
}

function minutesSince(iso: string, now: number): number {
  return Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
}

export function AppStatusPage() {
  const record = useAppStatusRecord();
  const lastChange = useAppStatusLastChange();
  return (
    <div className="flex min-w-0 flex-col gap-8">
      <PageHeader
        title="App status"
        description="Maintenance and app versions for the client and staff apps. Super Admin only, and every change is recorded in the audit log."
      />
      {record.isError ? (
        <Callout tone="danger" role="alert" title="App status could not be loaded">
          <RetryButton size="sm" onRetry={() => record.refetch()} />
        </Callout>
      ) : null}
      {record.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading app status…</p>
      ) : null}
      {record.data ? (
        <>
          <MaintenanceCard record={record.data} />
          <MinVersionCard record={record.data} />
        </>
      ) : null}
      {lastChange.data ? (
        <p className="text-sm text-muted-foreground">
          Last change: {lastChange.data.by ?? "the system"} ·{" "}
          {new Date(lastChange.data.at).toLocaleString("en-PH", {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "Asia/Manila",
          })}
        </p>
      ) : null}
    </div>
  );
}

function MaintenanceCard({ record }: { record: AppStatusRecord }) {
  const update = useUpdateAppStatus();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const phase = phaseOf(record, now);

  const [message, setMessage] = useState("");
  const [backBy, setBackBy] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // The form follows the saved row whenever it changes.
  useEffect(() => {
    setMessage(record.maintenance_message ?? DEFAULT_MESSAGE);
    setBackBy(
      record.maintenance_back_by
        ? toLocalInput(new Date(record.maintenance_back_by))
        : toLocalInput(new Date(Date.now() + 2 * 60 * 60 * 1000)),
    );
    setStartsAt(
      record.maintenance_starts_at
        ? toLocalInput(new Date(record.maintenance_starts_at))
        : // An hour ahead: inside the two-hour heads-up, and before the default
          // back-by above, so "Schedule" works without touching either field.
          toLocalInput(new Date(Date.now() + 60 * 60 * 1000)),
    );
    setScheduling(false);
  }, [record]);

  function save(patch: Parameters<typeof update.mutate>[0], done: string) {
    update.mutate(patch, {
      onSuccess: () => toast.success(done),
      onError: (error) => toast.error(error.message),
    });
  }

  function startNow() {
    const back = fromLocalInput(backBy);
    if (back && new Date(back).getTime() <= Date.now()) {
      toast.error("Expected back by has to be later than now.");
      return;
    }
    save(
      {
        maintenance_starts_at: new Date().toISOString(),
        maintenance_back_by: back,
        maintenance_message: message.trim() || null,
      },
      "Maintenance is on.",
    );
  }

  function schedule() {
    const start = fromLocalInput(startsAt);
    const back = fromLocalInput(backBy);
    if (!start || new Date(start).getTime() <= Date.now()) {
      toast.error("Pick a start time later than now.");
      return;
    }
    if (back && new Date(back) <= new Date(start)) {
      toast.error("Expected back by has to be after the start.");
      return;
    }
    save(
      {
        maintenance_starts_at: start,
        maintenance_back_by: back,
        maintenance_message: message.trim() || null,
      },
      "Maintenance scheduled.",
    );
  }

  function end(done: string) {
    save(
      { maintenance_starts_at: null, maintenance_back_by: null, maintenance_message: null },
      done,
    );
  }

  function saveDetails() {
    const back = fromLocalInput(backBy);
    const start = phase === "scheduled" ? fromLocalInput(startsAt) : record.maintenance_starts_at;
    if (back && start && new Date(back) <= new Date(start)) {
      toast.error("Expected back by has to be after the start.");
      return;
    }
    save(
      {
        ...(phase === "scheduled" ? { maintenance_starts_at: start } : {}),
        maintenance_back_by: back,
        maintenance_message: message.trim() || null,
      },
      "Saved. Open apps pick it up within a minute.",
    );
  }

  const fields = (
    <div className="grid gap-4 @2xl/canvas:grid-cols-2 sm:grid-cols-2">
      {phase === "scheduled" || (phase === "off" && scheduling) ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="mx-start">Starts</Label>
          <Input
            id="mx-start"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mx-back">Expected back by</Label>
        <Input
          id="mx-back"
          type="datetime-local"
          value={backBy}
          onChange={(e) => setBackBy(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <Label htmlFor="mx-message">Message people see</Label>
        <Input
          id="mx-message"
          maxLength={160}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
    </div>
  );

  const startsLabel = record.maintenance_starts_at
    ? maintenanceTimeLabel(new Date(record.maintenance_starts_at), now)
    : null;
  const backLabel = record.maintenance_back_by
    ? maintenanceTimeLabel(new Date(record.maintenance_back_by), now)
    : null;

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <CardTitle className="type-title-body">
            <h2>Maintenance</h2>
          </CardTitle>
          {phase === "off" ? <Badge variant="success">Off · apps normal</Badge> : null}
          {phase === "scheduled" ? <Badge variant="info">Scheduled {startsLabel}</Badge> : null}
          {phase === "on" ? <Badge variant="warning">On since {startsLabel}</Badge> : null}
          {phase === "overdue" ? <Badge variant="warning">On · past {backLabel}</Badge> : null}
        </div>
        <CardDescription className="max-w-prose leading-relaxed">
          {phase === "off"
            ? "Clients can book. Riders and therapists get new jobs."
            : phase === "scheduled"
              ? `Apps run normally until ${startsLabel}. People see a heads-up from two hours before (${maintenanceTimeLabel(
                  new Date(
                    new Date(record.maintenance_starts_at as string).getTime() - HEADS_UP_MS,
                  ),
                  now,
                )}).`
              : `Under maintenance for ${minutesSince(record.maintenance_starts_at as string, now)} min${
                  backLabel ? ` · back by ${backLabel}` : ""
                }. Clients can't book; jobs already running carry on.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {phase === "overdue" && record.maintenance_back_by ? (
          // "Back by" is not a timer (Leon, 2026-10-30): only End stops it, so
          // say plainly when the promised time has gone by.
          <Callout
            tone="warning"
            title={`Back-by time passed ${minutesSince(record.maintenance_back_by, now)} min ago`}
          >
            <div className="flex flex-col gap-3">
              <p className="text-sm leading-relaxed">
                Clients are seeing &quot;Taking a bit longer than planned&quot;. End maintenance if
                the fix is done, or set a new time below and save.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => end("Maintenance ended. Apps are back.")}>
                  End maintenance
                </Button>
              </div>
            </div>
          </Callout>
        ) : null}
        {fields}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-3">
        {phase === "off" && !scheduling ? (
          <>
            <Button
              variant="destructive"
              onClick={() => setConfirming(true)}
              loading={update.isPending}
            >
              Start maintenance now
            </Button>
            <Button variant="outline" onClick={() => setScheduling(true)}>
              Schedule for later
            </Button>
          </>
        ) : null}
        {phase === "off" && scheduling ? (
          <>
            <Button onClick={schedule} loading={update.isPending}>
              Schedule maintenance
            </Button>
            <Button variant="ghost" onClick={() => setScheduling(false)}>
              Back
            </Button>
          </>
        ) : null}
        {phase === "scheduled" ? (
          <>
            <Button onClick={saveDetails} loading={update.isPending}>
              Save changes
            </Button>
            <Button variant="outline" onClick={() => end("Schedule cancelled.")}>
              Cancel schedule
            </Button>
          </>
        ) : null}
        {phase === "on" || phase === "overdue" ? (
          <>
            <Button
              onClick={() => end("Maintenance ended. Apps are back.")}
              loading={update.isPending}
            >
              End maintenance
            </Button>
            <Button variant="outline" onClick={saveDetails}>
              Save changes
            </Button>
          </>
        ) : null}
      </CardFooter>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start maintenance now?</AlertDialogTitle>
            <AlertDialogDescription>
              Here is what changes the moment you start it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="flex flex-col gap-2 rounded-2xl bg-muted/50 p-4 text-sm leading-relaxed">
            <li>
              <b>Clients can&apos;t book.</b> They see the maintenance screen.
            </li>
            <li>
              <b>No new bookings</b> go to riders or therapists.
            </li>
            <li>
              <b>Jobs already in progress keep going:</b> sessions, trips and SOS work as usual.
            </li>
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                startNow();
              }}
            >
              Start maintenance
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function MinVersionCard({ record }: { record: AppStatusRecord }) {
  const update = useUpdateAppStatus();
  const [confirming, setConfirming] = useState(false);
  const required = record.min_build_at
    ? new Date(record.min_build_at).toLocaleString("en-PH", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Asia/Manila",
      })
    : null;

  function set(value: string | null, done: string) {
    update.mutate(
      { min_build_at: value },
      { onSuccess: () => toast.success(done), onError: (error) => toast.error(error.message) },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <CardTitle className="type-title-body">
          <h2>Require latest version</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          Apps older than this show a &quot;Please update&quot; screen. Use it only after a release
          that breaks old versions.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Callout tone="warning" title="Only after the release is uploaded">
          Press this only once the new release is live, or everyone gets stuck on the update screen.
        </Callout>
        <p className="text-sm text-muted-foreground">
          {required ? `Required: built after ${required}` : "Required: none"}
        </p>
      </CardContent>
      <CardFooter className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => setConfirming(true)}
          loading={update.isPending}
          disabled={!ADMIN_BUILD_AT}
        >
          Require this version
        </Button>
        {required ? (
          <Button variant="ghost" onClick={() => set(null, "Version requirement removed.")}>
            Remove requirement
          </Button>
        ) : null}
      </CardFooter>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Is the new release already live?</AlertDialogTitle>
            <AlertDialogDescription>
              Every app from an earlier release will show &quot;Please update&quot; until it loads
              the one this admin page is on. If that release is not uploaded to every app yet, there
              is nothing to update to. Reload this page first if you just released.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                if (ADMIN_BUILD_AT)
                  set(releaseMinimum(ADMIN_BUILD_AT), "Older apps will now ask to update.");
              }}
            >
              Yes, require it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
